import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  Button,
  Input,
  Badge,
  Table,
  Spinner,
  Flex,
  Alert,
  NativeSelect,
  SimpleGrid,
} from '@chakra-ui/react';
import { userService } from '../services/userService';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';

const API_URL = config.BACKEND_URL;

const Settings = () => {
  const { hasRole } = useAuth();
  const [groups, setGroups] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('');

  // Form state
  const [newGroup, setNewGroup] = useState({
    groupId: '',
    groupName: '',
    role: 'User'
  });

  // Approver form state
  const [newApprover, setNewApprover] = useState({
    approver_email: '',
    approver_name: ''
  });

  const [editingApprover, setEditingApprover] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    loadGroups();
    loadApprovers();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await userService.getGroups();
      setGroups(response.data);
    } catch (error) {
      console.error('Error loading groups:', error);
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const loadApprovers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/approver-config`, {
        withCredentials: true
      });
      setApprovers(response.data);
    } catch (error) {
      console.error('Error loading approvers:', error);
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();

    if (!newGroup.groupId || !newGroup.groupName || !newGroup.role) {
      setMessage('Group ID, Group Name, and Role are required.');
      setMessageType('error');
      return;
    }

    try {
      await userService.addGroup(newGroup);
      setMessage(`Group added successfully with ${newGroup.role} role.`);
      setMessageType('success');
      setNewGroup({ groupId: '', groupName: '', role: 'User' });
      loadGroups();
    } catch (error) {
      console.error('Error adding group:', error);
      setMessage(error.response?.data?.error || 'Failed to add group');
      setMessageType('error');
    }
  };

  const handleRemoveGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to remove this group?')) {
      return;
    }

    try {
      await userService.removeGroup(groupId);
      setMessage('Group removed successfully.');
      setMessageType('success');
      loadGroups();
    } catch (error) {
      console.error('Error removing group:', error);
      setMessage(error.response?.data?.error || 'Failed to remove group');
      setMessageType('error');
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();

    if (!editingGroup || !editingGroup.role) {
      setMessage('Role is required.');
      setMessageType('error');
      return;
    }

    try {
      await userService.addGroup({
        groupId: editingGroup.group_id,
        groupName: editingGroup.group_name,
        role: editingGroup.role
      });
      setMessage(`Group role updated to ${editingGroup.role} successfully.`);
      setMessageType('success');
      setEditingGroup(null);
      loadGroups();
    } catch (error) {
      console.error('Error updating group:', error);
      setMessage(error.response?.data?.error || 'Failed to update group');
      setMessageType('error');
    }
  };

  // Approver management functions
  const handleAddApprover = async (e) => {
    e.preventDefault();

    if (!newApprover.approver_email || !newApprover.approver_name) {
      setMessage('Approver email and name are required.');
      setMessageType('error');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/approver-config`, newApprover, {
        withCredentials: true
      });
      setMessage('Approver added successfully.');
      setMessageType('success');
      setNewApprover({ approver_email: '', approver_name: '' });
      loadApprovers();
    } catch (error) {
      console.error('Error adding approver:', error);
      setMessage(error.response?.data?.error || 'Failed to add approver');
      setMessageType('error');
    }
  };

  const handleToggleApproverStatus = async (id, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/approver-config/${id}/status`, {
        is_active: !currentStatus
      }, {
        withCredentials: true
      });
      setMessage(`Approver ${!currentStatus ? 'activated' : 'deactivated'} successfully.`);
      setMessageType('success');
      loadApprovers();
    } catch (error) {
      console.error('Error updating approver status:', error);
      setMessage(error.response?.data?.error || 'Failed to update approver status');
      setMessageType('error');
    }
  };

  const handleUpdateApprover = async (e) => {
    e.preventDefault();

    if (!editingApprover.approver_email || !editingApprover.approver_name) {
      setMessage('Approver email and name are required.');
      setMessageType('error');
      return;
    }

    try {
      await axios.put(`${API_URL}/api/approver-config/${editingApprover.id}`, {
        approver_email: editingApprover.approver_email,
        approver_name: editingApprover.approver_name
      }, {
        withCredentials: true
      });
      setMessage('Approver updated successfully.');
      setMessageType('success');
      setEditingApprover(null);
      loadApprovers();
    } catch (error) {
      console.error('Error updating approver:', error);
      setMessage(error.response?.data?.error || 'Failed to update approver');
      setMessageType('error');
    }
  };

  const handleDeleteApprover = async (id) => {
    if (!window.confirm('Are you sure you want to delete this approver?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/approver-config/${id}`, {
        withCredentials: true
      });
      setMessage('Approver deleted successfully.');
      setMessageType('success');
      loadApprovers();
    } catch (error) {
      console.error('Error deleting approver:', error);
      setMessage(error.response?.data?.error || 'Failed to delete approver');
      setMessageType('error');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Admin': return 'red';
      case 'Editor': return 'orange';
      default: return 'blue';
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.xl">
          <Flex justify="center" align="center" py={20}>
            <VStack spacing={4}>
              <Spinner size="xl" color="purple.500" thickness="4px" />
              <Text color="gray.500">Loading settings...</Text>
            </VStack>
          </Flex>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box textAlign="center" py={4}>
            <Heading as="h1" size="2xl" color="purple.600" mb={3}>Settings</Heading>
            <Text fontSize="xl" color="gray.500">Manage Microsoft Entra (Azure AD) groups and their role assignments.</Text>
          </Box>

          {/* Messages */}
          {error && (
            <Alert.Root status="error" borderRadius="md">
              <Alert.Indicator />
              {error}
            </Alert.Root>
          )}

          {message && (
            <Alert.Root status={messageType === 'success' ? 'success' : 'error'} borderRadius="md">
              <Alert.Indicator />
              {message}
            </Alert.Root>
          )}

          {/* Add Group Card */}
          <Card.Root shadow="lg" borderRadius="xl" borderTop="4px" borderTopColor="purple.500">
            <Card.Header>
              <Heading size="md" color="gray.700">Add Group with Role Assignment</Heading>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Configure Microsoft Entra groups and automatically assign roles to new users from these groups.
              </Text>
            </Card.Header>
            <Card.Body>
              <form onSubmit={handleAddGroup}>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap={6} rowGap={6}>
                  <Box>
                    <Text fontWeight="bold" mb={2}>Group ID (from Azure AD)</Text>
                    <Input
                      value={newGroup.groupId}
                      onChange={(e) => setNewGroup({ ...newGroup, groupId: e.target.value })}
                      placeholder="e.g., 6b5c0177-1067-4557-b642-b9ec54aba5db"
                      required
                    />
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={2}>Group Name</Text>
                    <Input
                      value={newGroup.groupName}
                      onChange={(e) => setNewGroup({ ...newGroup, groupName: e.target.value })}
                      placeholder="e.g., IT Department"
                      required
                    />
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={2}>Auto-Assign Role</Text>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={newGroup.role}
                        onChange={(e) => setNewGroup({ ...newGroup, role: e.target.value })}
                      >
                        <option value="User">User (Read-only)</option>
                        <option value="User Branch">User Branch (Branch read-only)</option>
                        <option value="Editor">Editor (Can upload & edit)</option>
                        <option value="Editor Branch">Editor Branch (Branch only)</option>
                        <option value="Admin">Admin (Full access)</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Box>
                </SimpleGrid>
                <Button type="submit" colorPalette="purple" mt={6}>Add Group</Button>
              </form>
            </Card.Body>
          </Card.Root>

          {/* Configured Groups Card */}
          <Card.Root shadow="lg" borderRadius="xl" borderTop="4px" borderTopColor="blue.500">
            <Card.Header>
              <Heading size="md" color="gray.700">Configured Groups & Roles</Heading>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Users must belong to at least one of these groups to access the system.
              </Text>
            </Card.Header>
            <Card.Body>
              {groups.length === 0 ? (
                <Alert.Root status="info" borderRadius="md">
                  <Alert.Indicator />
                  No groups configured. Add groups above to control access and assign roles.
                </Alert.Root>
              ) : (
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Group Name</Table.ColumnHeader>
                      <Table.ColumnHeader>Group ID</Table.ColumnHeader>
                      <Table.ColumnHeader>Auto-Assign Role</Table.ColumnHeader>
                      <Table.ColumnHeader>Actions</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {groups.map((group, index) => (
                      <Table.Row key={index}>
                        <Table.Cell fontWeight="medium">{group.group_name}</Table.Cell>
                        <Table.Cell fontFamily="mono" fontSize="xs">{group.group_id}</Table.Cell>
                        <Table.Cell>
                          {editingGroup && editingGroup.group_id === group.group_id ? (
                            <NativeSelect.Root size="sm">
                              <NativeSelect.Field
                                value={editingGroup.role}
                                onChange={(e) => setEditingGroup({ ...editingGroup, role: e.target.value })}
                              >
                                <option value="User">User</option>
                                <option value="User Branch">User Branch</option>
                                <option value="Editor">Editor</option>
                                <option value="Editor Branch">Editor Branch</option>
                                <option value="Admin">Admin</option>
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          ) : (
                            <Badge colorPalette={getRoleBadgeColor(group.role)}>{group.role}</Badge>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {editingGroup && editingGroup.group_id === group.group_id ? (
                            <HStack>
                              <Button size="xs" colorPalette="green" onClick={handleUpdateGroup}>Save</Button>
                              <Button size="xs" variant="outline" onClick={() => setEditingGroup(null)}>Cancel</Button>
                            </HStack>
                          ) : (
                            <HStack>
                              <Button size="xs" variant="outline" colorPalette="purple" onClick={() => setEditingGroup(group)}>Edit Role</Button>
                              <Button size="xs" colorPalette="red" variant="outline" onClick={() => handleRemoveGroup(group.group_id)}>Remove</Button>
                            </HStack>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              )}
            </Card.Body>
          </Card.Root>

          {/* Upload Approval Configuration - Admin Only */}
          {hasRole(['Admin']) && (
            <Card.Root shadow="lg" borderRadius="xl" borderTop="4px" borderTopColor="orange.500">
              <Card.Header>
                <Heading size="md" color="gray.700">Upload Approval Configuration</Heading>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Configure the email address that will receive upload approval notifications.
                </Text>
              </Card.Header>
              <Card.Body>
                <Alert.Root status="info" borderRadius="md" mb={6}>
                  <Alert.Indicator />
                  <Text><strong>Note:</strong> Only one approver can be active at a time.</Text>
                </Alert.Root>

                {/* Add Approver Form */}
                <form onSubmit={handleAddApprover}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} rowGap={6} mb={6}>
                    <Box>
                      <Text fontWeight="bold" mb={2}>Approver Email</Text>
                      <Input
                        type="email"
                        value={newApprover.approver_email}
                        onChange={(e) => setNewApprover({ ...newApprover, approver_email: e.target.value })}
                        placeholder="approver@company.com"
                        disabled={approvers.some(a => a.is_active)}
                      />
                    </Box>
                    <Box>
                      <Text fontWeight="bold" mb={2}>Approver Name</Text>
                      <Input
                        value={newApprover.approver_name}
                        onChange={(e) => setNewApprover({ ...newApprover, approver_name: e.target.value })}
                        placeholder="John Doe"
                        disabled={approvers.some(a => a.is_active)}
                      />
                    </Box>
                  </SimpleGrid>
                  <Button
                    type="submit"
                    colorPalette="purple"
                    disabled={approvers.some(a => a.is_active)}
                  >
                    Add Approver
                  </Button>
                  {approvers.some(a => a.is_active) && (
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Deactivate the current approver to add a new one.
                    </Text>
                  )}
                </form>

                {/* Approvers Table */}
                <Heading size="sm" mt={8} mb={4}>Current Approvers</Heading>
                {approvers.length === 0 ? (
                  <Alert.Root status="info" borderRadius="md">
                    <Alert.Indicator />
                    No approvers configured.
                  </Alert.Root>
                ) : (
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Name</Table.ColumnHeader>
                        <Table.ColumnHeader>Email</Table.ColumnHeader>
                        <Table.ColumnHeader>Status</Table.ColumnHeader>
                        <Table.ColumnHeader>Actions</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {approvers.map((approver) => (
                        <Table.Row key={approver.id}>
                          <Table.Cell>
                            {editingApprover && editingApprover.id === approver.id ? (
                              <Input
                                size="sm"
                                value={editingApprover.approver_name}
                                onChange={(e) => setEditingApprover({ ...editingApprover, approver_name: e.target.value })}
                              />
                            ) : (
                              approver.approver_name
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {editingApprover && editingApprover.id === approver.id ? (
                              <Input
                                size="sm"
                                type="email"
                                value={editingApprover.approver_email}
                                onChange={(e) => setEditingApprover({ ...editingApprover, approver_email: e.target.value })}
                              />
                            ) : (
                              <Text fontFamily="mono" fontSize="xs">{approver.approver_email}</Text>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette={approver.is_active ? 'green' : 'gray'}>
                              {approver.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            {editingApprover && editingApprover.id === approver.id ? (
                              <HStack>
                                <Button size="xs" colorPalette="green" onClick={handleUpdateApprover}>Save</Button>
                                <Button size="xs" variant="outline" onClick={() => setEditingApprover(null)}>Cancel</Button>
                              </HStack>
                            ) : (
                              <HStack>
                                <Button size="xs" variant="outline" colorPalette="purple" onClick={() => setEditingApprover(approver)}>Edit</Button>
                                <Button
                                  size="xs"
                                  colorPalette={approver.is_active ? 'orange' : 'green'}
                                  variant="outline"
                                  onClick={() => handleToggleApproverStatus(approver.id, approver.is_active)}
                                >
                                  {approver.is_active ? 'Deactivate' : 'Activate'}
                                </Button>
                                <Button size="xs" colorPalette="red" variant="solid" onClick={() => handleDeleteApprover(approver.id)}>Delete</Button>
                              </HStack>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                )}
              </Card.Body>
            </Card.Root>
          )}

          {/* Help Card */}
          <Card.Root shadow="lg" borderRadius="xl">
            <Card.Header>
              <Heading size="md" color="gray.700">How to Find Group IDs</Heading>
            </Card.Header>
            <Card.Body>
              <VStack align="start" spacing={2}>
                <Text>1. Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" style={{ color: '#805AD5' }}>Azure Portal</a></Text>
                <Text>2. Navigate to Microsoft Entra ID (formerly Azure AD)</Text>
                <Text>3. Go to Groups</Text>
                <Text>4. Select the group you want to add</Text>
                <Text>5. Copy the "Object ID" from the group's overview page</Text>
              </VStack>

              <Box mt={6} p={4} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                <Heading size="sm" color="orange.700" mb={2}>⚠️ Important Setup Required</Heading>
                <Text fontSize="sm" color="orange.700">
                  The database currently contains sample group IDs. You need to replace these with your actual Azure AD group IDs.
                </Text>
              </Box>
            </Card.Body>
          </Card.Root>
        </VStack>
      </Container>
    </Box>
  );
};

export default Settings;